// Purpose: shared routing-target picker used by both NewEphemeralDropbox
// and NewPersistentDropbox (Phase 6 D3 reversal). Lifts all Graph
// wiring, folder-path live validation, and notification toggles out of
// the form pages so the two callers stay small.

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext.tsx';
import type { RoutingConfigInput, RoutingTarget } from '../api/dropbox.ts';
import { listDrives, listSites, type DriveSummary, type SiteSummary } from '../api/graph.ts';

export interface RoutingPickerValue {
  config: RoutingConfigInput | undefined; // undefined when target=EPHEMERAL
  incomplete: boolean; // true while required fields are missing
  folderPathWarning: string | null;
}

export interface RoutingPickerProps {
  value: RoutingPickerValue;
  onChange: (next: RoutingPickerValue) => void;
  onError?: (msg: string) => void;
}

export function RoutingPicker(props: RoutingPickerProps) {
  // Commit 8b.19: dropbox.routing.enabled flag gates the full picker. When
  // OFF the SPA renders a static explainer line and emits a clean
  // EPHEMERAL-equivalent value, so the form parent keeps working without
  // a target dropdown. Server is authoritative — a direct API call still
  // 400s with routing_disabled even if the SPA somehow renders the picker.
  const { user } = useAuth();
  const routingEnabled = user?.featureFlags?.dropboxRoutingEnabled ?? false;
  if (!routingEnabled) {
    return <RoutingPickerDisabled onChange={props.onChange} />;
  }
  return <RoutingPickerInner onChange={props.onChange} onError={props.onError} />;
}

interface InnerProps {
  onChange: (next: RoutingPickerValue) => void;
  onError?: ((msg: string) => void) | undefined;
}

function RoutingPickerDisabled({ onChange }: Pick<InnerProps, 'onChange'>) {
  // Emit once on mount. Matches the dep-stability pattern of
  // RoutingPickerInner, which deliberately tracks state values rather than
  // onChange identity so a parent passing a fresh callback each render does
  // not re-fire the emit.
  useEffect(() => {
    onChange({ config: undefined, incomplete: false, folderPathWarning: null });
  }, []);
  return (
    <div className="text-sm text-fg-muted">
      Files uploaded to this dropbox will be retained in AST. SharePoint and OneDrive routing are
      temporarily disabled.
    </div>
  );
}

function RoutingPickerInner({ onChange, onError }: InnerProps) {
  const [target, setTarget] = useState<RoutingTarget>('EPHEMERAL');
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [drives, setDrives] = useState<DriveSummary[]>([]);
  const [siteId, setSiteId] = useState('');
  const [driveId, setDriveId] = useState('');
  const [oneDriveMode, setOneDriveMode] = useState<'personal' | 'shared'>('personal');
  const [folderPath, setFolderPath] = useState('');
  const [notifyOnSuccess, setNotifyOnSuccess] = useState(false);
  const [notifyOnFailure, setNotifyOnFailure] = useState(true);

  useEffect(() => {
    if (target === 'SHAREPOINT' && sites.length === 0) {
      void listSites()
        .then(setSites)
        .catch((err) => onError?.(`sites: ${(err as Error).message}`));
      void listDrives()
        .then(setDrives)
        .catch((err) => onError?.(`drives: ${(err as Error).message}`));
    }
    if (target === 'ONEDRIVE' && oneDriveMode === 'shared' && drives.length === 0) {
      void listDrives()
        .then(setDrives)
        .catch((err) => onError?.(`drives: ${(err as Error).message}`));
    }
  }, [target, oneDriveMode, sites.length, drives.length, onError]);

  const folderPathWarning = validateFolderPathLive(folderPath);
  const selectedSiteDrives = drives.filter((d) => d.siteId === siteId);
  const sharedDrives = drives.filter((d) => d.driveType !== 'documentLibrary');

  // Derive the picker's current value and notify upward on every change.
  // Intentionally omits `onChange` + `folderPathWarning` from the dep
  // array to avoid infinite re-renders when parents pass a fresh
  // onChange each render; the inputs that matter are the local state
  // values we track explicitly.
  useEffect(() => {
    const config = buildConfig();
    const incomplete =
      (target === 'SHAREPOINT' && (!siteId || !driveId)) ||
      (target === 'ONEDRIVE' && oneDriveMode === 'shared' && !driveId);
    onChange({ config, incomplete, folderPathWarning });
  }, [target, siteId, driveId, oneDriveMode, folderPath, notifyOnSuccess, notifyOnFailure]);

  function buildConfig(): RoutingConfigInput | undefined {
    if (target === 'EPHEMERAL') return undefined;
    const base: RoutingConfigInput = {
      target,
      notifyOnSuccess,
      notifyOnFailure,
    };
    if (folderPath.trim().length > 0) base.folderPath = folderPath.trim();
    if (target === 'SHAREPOINT') {
      base.siteId = siteId;
      base.driveId = driveId;
    } else if (target === 'ONEDRIVE' && oneDriveMode === 'shared') {
      base.driveId = driveId;
    }
    return base;
  }

  return (
    <>
      <label className="block">
        <div className="text-sm font-medium mb-1">Routing</div>
        <select
          value={target}
          onChange={(e) => {
            setTarget(e.target.value as RoutingTarget);
            setSiteId('');
            setDriveId('');
          }}
          className="border border-border-default rounded-md px-3 py-2"
        >
          <option value="EPHEMERAL">Keep on AST (no routing)</option>
          <option value="SHAREPOINT">Route to SharePoint</option>
          <option value="ONEDRIVE">Route to OneDrive</option>
        </select>
      </label>

      {target === 'SHAREPOINT' && (
        <>
          <label className="block">
            <div className="text-sm font-medium mb-1">SharePoint site</div>
            <select
              value={siteId}
              onChange={(e) => {
                setSiteId(e.target.value);
                setDriveId('');
              }}
              className="w-full border border-border-default rounded-md px-3 py-2"
            >
              <option value="">Select a site</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-sm font-medium mb-1">Document library</div>
            <select
              value={driveId}
              onChange={(e) => setDriveId(e.target.value)}
              disabled={!siteId}
              className="w-full border border-border-default rounded-md px-3 py-2 disabled:opacity-50"
            >
              <option value="">Select a library</option>
              {selectedSiteDrives.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      {target === 'ONEDRIVE' && (
        <>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={oneDriveMode === 'personal'}
                onChange={() => {
                  setOneDriveMode('personal');
                  setDriveId('');
                }}
              />
              My OneDrive
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={oneDriveMode === 'shared'}
                onChange={() => setOneDriveMode('shared')}
              />
              Shared drive
            </label>
          </div>
          {oneDriveMode === 'shared' && (
            <label className="block">
              <div className="text-sm font-medium mb-1">Shared drive</div>
              <select
                value={driveId}
                onChange={(e) => setDriveId(e.target.value)}
                className="w-full border border-border-default rounded-md px-3 py-2"
              >
                <option value="">Select a shared drive</option>
                {sharedDrives.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </>
      )}

      {target !== 'EPHEMERAL' && (
        <>
          <label className="block">
            <div className="text-sm font-medium mb-1">
              Folder path <span className="text-fg-muted">(optional)</span>
            </div>
            <input
              type="text"
              value={folderPath}
              onChange={(e) => setFolderPath(e.target.value)}
              placeholder="e.g. inbox/resumes"
              maxLength={200}
              className="w-full border border-border-default rounded-md px-3 py-2"
            />
            {folderPathWarning ? (
              <div className="text-status-warning text-body-s mt-1">{folderPathWarning}</div>
            ) : null}
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={notifyOnSuccess}
              onChange={(e) => setNotifyOnSuccess(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm">Email me when a file is successfully routed.</span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={notifyOnFailure}
              onChange={(e) => setNotifyOnFailure(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm">
              Email me if a routing attempt fails and the file can&rsquo;t be delivered.
            </span>
          </label>
        </>
      )}
    </>
  );
}

// Client-side preview of the server's normalizeFolderPath. Server is
// authoritative; this only surfaces obvious problems inline.
function validateFolderPathLive(input: string): string | null {
  if (input.length === 0) return null;
  // eslint-disable-next-line no-control-regex
  if (/\u0000/.test(input)) return 'Null byte is not allowed.';
  if (input.startsWith('/')) return 'Folder path must not start with a slash.';
  if (input.split('/').some((seg) => seg === '..' || seg === '.')) {
    return 'Folder path may not include .. or . segments.';
  }
  if (input.length > 200) return 'Folder path must be 200 characters or fewer.';
  return null;
}
